import type {
  AreaDto,
  AreaInput,
  EmployeeDto,
  EmployeeInput,
  EmployeePatch,
  LoginAttemptDto,
  VncSessionDetailDto,
  MeDto,
  PanelAdminDto,
  PanelCheckDto,
  PanelDto,
  PanelInput,
  VncMode,
  VncSessionDto,
  VncTicketDto,
} from '@app/shared';
import { platform } from './platform';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Картинка из API (снимки экрана): <img> не умеет передавать авторизацию, поэтому через fetch → blob. */
export async function fetchImageUrl(path: string): Promise<string> {
  const res = await fetch(`/api${path}`, { headers: { authorization: platform.authHeader() } });
  if (!res.ok) throw new ApiError(res.status, 'error', 'Снимок не загрузился');
  return URL.createObjectURL(await res.blob());
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: { authorization: platform.authHeader(), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет.');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Что-то пошло не так');
  return data as T;
}

/** Адрес WebSocket для билета: тот же сайт, wss на https. */
export function vncUrl(path: string): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${path}`;
}

export const api = {
  me: () => request<MeDto>('GET', '/me'),

  staff: () => request<EmployeeDto[]>('GET', '/staff'),
  loginAttempts: () => request<LoginAttemptDto[]>('GET', '/staff/attempts'),
  createEmployee: (e: EmployeeInput) => request<EmployeeDto>('POST', '/staff', e),
  patchEmployee: (id: string, patch: EmployeePatch) => request<EmployeeDto>('PATCH', `/staff/${id}`, patch),
  deleteEmployee: (id: string) => request<void>('DELETE', `/staff/${id}`),

  areas: () => request<AreaDto[]>('GET', '/areas'),
  createArea: (a: AreaInput) => request<AreaDto>('POST', '/areas', a),
  updateArea: (id: string, a: Partial<AreaInput>) => request<AreaDto>('PATCH', `/areas/${id}`, a),
  deleteArea: (id: string) => request<void>('DELETE', `/areas/${id}`),

  panels: () => request<PanelDto[]>('GET', '/panels'),
  adminPanels: () => request<PanelAdminDto[]>('GET', '/admin/panels'),
  createPanel: (p: PanelInput) => request<PanelAdminDto>('POST', '/panels', p),
  updatePanel: (id: string, p: Partial<PanelInput>) => request<PanelAdminDto>('PATCH', `/panels/${id}`, p),
  deletePanel: (id: string) => request<void>('DELETE', `/panels/${id}`),
  checkPanel: (id: string | null, p: Partial<Pick<PanelInput, 'host' | 'port' | 'password'>>) =>
    request<PanelCheckDto>('POST', id ? `/panels/${id}/check` : '/panels/check', p),
  session: (id: string, mode: VncMode) => request<VncTicketDto>('POST', `/panels/${id}/session`, { mode }),
  sessions: (f: { panelId?: string; employeeId?: string; from?: string; to?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(f).filter((e): e is [string, string] => !!e[1])).toString();
    return request<VncSessionDto[]>('GET', `/sessions${q ? `?${q}` : ''}`);
  },
  sessionDetail: (id: string) => request<VncSessionDetailDto>('GET', `/sessions/${id}`),
  /** Снимок как есть (image/jpeg), без base64. */
  saveShot: async (id: string, jpeg: Blob) => {
    const res = await fetch(`/api/sessions/${id}/shots`, { method: 'POST', headers: { authorization: platform.authHeader(), 'content-type': 'image/jpeg' }, body: jpeg });
    if (res.status === 415) {
      // Сервер старой версии принимает только JSON {dataUrl}.
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(jpeg);
      });
      await request<unknown>('POST', `/sessions/${id}/shots`, { dataUrl });
      return;
    }
    if (!res.ok && res.status !== 204) throw new ApiError(res.status, 'error', 'Снимок не сохранился');
  },
  terminate: (id: string) => request<{ ok: boolean }>('POST', `/sessions/${id}/terminate`, {}),
};
